import React, { useState, useEffect } from "react";
import {
  SearchIcon,
  Plus,
  Settings,
  RotateCw,
  X,
  FilterX,
  Eye,
  GripVertical,
  ChevronDown,
  ChevronRight,
  Pencil,
  Trash,
  ChevronUp,
  ListIcon,
} from "lucide-react";
import {
  TextField,
  IconButton,
  MenuItem,
  Box,
  Modal,
  Menu,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import "./styles/productStyles.css";
import BreadCrumbsContainer from "../../Common/Breadcrumbs";
import CategoryCard from "./Components/CategoryCard";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
  useFetchCategoryTreeQuery,
  useCreateCategoryMutation,
  useLazyFetchVendorStoreQuery,
  useEditCategoryMutation,
  useDeleteCategoryMutation,
} from "../../../services/baseQuery";
import { errorToast, successToast } from "../../../services/toast.service";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 800,
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: 0,
  maxHeight: "90vh",
  overflow: "auto",
};

const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png"];
const ALLOWED_EXTENSIONS = [".jpg", ".jpeg", ".png"];

const Category = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({
    status: "ALL",
    parentOnly: false,
  });
  const [addModal, setAddModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isReordering, setIsReordering] = useState(false);
  const [viewType, setViewType] = useState("list");
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    parent_category: "",
    display_order: 0,
    gst_rate: 18,
  });
  const [editModal, setEditModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: "",
    description: "",
    parent_category: "",
    display_order: 0,
    gst_rate: 18,
    status: "ACTIVE",
  });
  const [editImage, setEditImage] = useState(null);
  const [editPreviewUrl, setEditPreviewUrl] = useState(null);
  // Add delete modal state
  const [deleteModal, setDeleteModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  // Update the query parameters state
  const [queryParams, setQueryParams] = useState({
    status: "ALL",
    search: "",
    sort: "", // Will be in format: "field_order"
    parent_only: false,
    limit: 50,
    page: 1,
  });

  // Update the API query to include these parameters
  const {
    data: categoryData,
    isLoading,
    isError,
    refetch: refetchCategories,
  } = useFetchCategoryTreeQuery({
    status: queryParams.status,
    search: queryParams.search,
    sort: queryParams.sort,
    parent_only: queryParams.parent_only,
    limit: queryParams.limit,
    page: queryParams.page,
  });

  // Get store ID
  const [triggerFetchStore, { data: storeData }] =
    useLazyFetchVendorStoreQuery();
  const [createCategory, { isLoading: isCreating }] =
    useCreateCategoryMutation();
  const [editCategory, { isLoading: isEditing }] = useEditCategoryMutation();
  const [deleteCategory, { isLoading: isDeleting }] =
    useDeleteCategoryMutation();

  // First, add a refreshing state
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    triggerFetchStore();
  }, []);

  // Transform API data to match our component structure
  const transformCategories = apiCategories => {
    return apiCategories?.map(category => ({
      id: category._id,
      name: category.name,
      status: category.status,
      gst: category.gst_rate,
      products: category.products_count,
      description: category.description,
      display_order: category.display_order,
      image: category.image,
      createdAt: category.createdAt,
      updatedAt: category.updatedAt,
      children: category.children?.map(child => ({
        id: child._id,
        name: child.name,
        status: child.status,
        gst: child.gst_rate,
        products: child.products_count,
        parent: category._id,
        description: child.description,
        display_order: child.display_order,
        image: child.image,
        createdAt: child.createdAt,
        updatedAt: child.updatedAt,
      })),
    }));
  };

  const categories = transformCategories(categoryData?.data?.categories) || [];

  // Add state for expanded categories
  const [expandedCategories, setExpandedCategories] = useState({});

  // Toggle category expansion
  const toggleCategoryExpansion = categoryId => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  // First, add state for settings menu
  const [anchorEl, setAnchorEl] = useState(null);

  const handleSettingsClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseSettings = () => {
    setAnchorEl(null);
  };

  // Update the effect to preserve manual expansions
  useEffect(() => {
    if (!queryParams.search) {
      // Don't reset expansions when search is cleared
      return;
    }

    const searchTerm = queryParams.search.toLowerCase();
    const newExpandedState = {};

    // Check each category and its children
    categories.forEach(category => {
      // Check if any child matches the search
      const hasMatchingChild = category.children?.some(child =>
        child.name.toLowerCase().includes(searchTerm),
      );

      // If any child matches, expand the parent
      if (hasMatchingChild) {
        newExpandedState[category.id] = true;
      }
    });

    // Update expanded state while preserving manual expansions
    setExpandedCategories(prev => ({
      ...prev,
      ...newExpandedState,
    }));
  }, [queryParams.search, categories]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-200px)]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#5652B0]" />
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-200px)]">
        <p className="text-red-500 mb-2">Failed to load categories</p>
        <button
          onClick={refetchCategories}
          className="px-4 py-2 bg-[#5652B0] text-white rounded-lg text-sm font-medium hover:bg-[#4A47A3]">
          Retry
        </button>
      </div>
    );
  }

  const handleImageSelect = event => {
    const file = event.target.files[0];
    if (file) {
      // Get file extension
      const fileExtension = file.name
        .toLowerCase()
        .substring(file.name.lastIndexOf("."));

      // Check both MIME type and extension
      if (
        !ALLOWED_IMAGE_TYPES.includes(file.type) ||
        !ALLOWED_EXTENSIONS.includes(fileExtension)
      ) {
        errorToast("Only JPG and PNG files are allowed!");
        event.target.value = ""; // Reset input
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        errorToast("Image size should be less than 5MB");
        event.target.value = ""; // Reset input
        return;
      }

      setSelectedImage(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleAddCategory = async () => {
    try {
      if (!formData.name.trim()) {
        errorToast("Category name is required");
        return;
      }

      if (!storeData?.data?.store?._id) {
        errorToast("Store information not available");
        return;
      }

      const submitFormData = new FormData();
      submitFormData.append("name", formData.name);
      submitFormData.append("store", storeData.data.store._id);
      submitFormData.append("gst_rate", formData.gst_rate);
      submitFormData.append("display_order", formData.display_order);

      if (formData.description) {
        submitFormData.append("description", formData.description);
      }

      if (formData.parent_category) {
        submitFormData.append("parent_category", formData.parent_category);
      }

      if (selectedImage) {
        submitFormData.append("image", selectedImage);
      }

      const response = await createCategory(submitFormData).unwrap();

      if (response.success) {
        successToast("Category created successfully");
        setAddModal(false);
        // Reset form
        setFormData({
          name: "",
          description: "",
          parent_category: "",
          display_order: 0,
          gst_rate: 18,
        });
        setSelectedImage(null);
        setPreviewUrl(null);
      } else {
        console.log(response, "response err");
        errorToast(response.message || "Failed to create category");
      }
    } catch (error) {
      errorToast(error.data?.error?.message || "Failed to create category");
    }
  };

  const handleViewCategory = category => {
    setSelectedCategory(category);
    setViewModal(true);
  };

  // Handle drag end
  const handleDragEnd = result => {
    if (!result.destination) return;

    const items = Array.from(categories);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update display order for each item
    const updatedItems = items.map((item, index) => ({
      ...item,
      display_order: index,
    }));

    // Here you would typically make an API call to update the order in the backend
  };

  const handleEditClick = category => {
    setEditingCategory(category);
    setEditFormData({
      name: category.name,
      description: category.description || "",
      parent_category: category.parent || "",
      display_order: category.display_order || 0,
      gst_rate: category.gst,
      status: category.status,
    });
    setEditPreviewUrl(category.image);
    setEditModal(true);
  };

  const handleEditImageSelect = event => {
    const file = event.target.files[0];
    if (file) {
      const fileExtension = file.name
        .toLowerCase()
        .substring(file.name.lastIndexOf("."));

      if (
        !ALLOWED_IMAGE_TYPES.includes(file.type) ||
        !ALLOWED_EXTENSIONS.includes(fileExtension)
      ) {
        errorToast("Only JPG and PNG files are allowed!");
        event.target.value = ""; // Reset input
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        errorToast("Image size should be less than 5MB");
        event.target.value = ""; // Reset input
        return;
      }

      setEditImage(file);
      setEditPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleEditCategory = async () => {
    try {
      if (!editFormData.name.trim()) {
        errorToast("Category name is required");
        return;
      }

      console.log("Editing category:", editingCategory.id); // Debug log

      const submitFormData = new FormData();
      submitFormData.append("name", editFormData.name);
      submitFormData.append("gst_rate", editFormData.gst_rate);
      submitFormData.append("display_order", editFormData.display_order);
      submitFormData.append("status", editFormData.status);

      if (editFormData.description) {
        submitFormData.append("description", editFormData.description);
      }

      if (editFormData.parent_category) {
        submitFormData.append("parent_category", editFormData.parent_category);
      }

      if (editImage) {
        submitFormData.append("image", editImage);
      }

      console.log("Submitting form data:", {
        categoryId: editingCategory.id,
        name: editFormData.name,
        gst_rate: editFormData.gst_rate,
        status: editFormData.status,
      }); // Debug log

      const response = await editCategory({
        categoryId: editingCategory.id,
        formData: submitFormData,
      }).unwrap();

      console.log("Edit response:", response); // Debug log

      if (response.success) {
        successToast("Category updated successfully");
        setEditModal(false);
        // Reset form
        setEditingCategory(null);
        setEditFormData({
          name: "",
          description: "",
          parent_category: "",
          display_order: 0,
          gst_rate: 18,
          status: "ACTIVE",
        });
        setEditImage(null);
        setEditPreviewUrl(null);
      } else {
        errorToast(
          response?.data?.error?.message || "Failed to update category",
        );
      }
    } catch (error) {
      console.error("Edit error:", error); // Debug log
      errorToast(error.data?.error?.message || "Failed to update category");
    }
  };

  // Add delete handler
  const handleDeleteCategory = async () => {
    try {
      if (!categoryToDelete) return;

      const response = await deleteCategory(categoryToDelete.id).unwrap();

      if (response.success) {
        successToast("Category deleted successfully");
        setDeleteModal(false);
        setCategoryToDelete(null);
      } else {
        errorToast(response.message || "Failed to delete category");
      }
    } catch (error) {
      errorToast(error.data?.error?.message || "Failed to delete category");
    }
  };

  // Add function to open delete modal
  const handleDeleteClick = category => {
    setCategoryToDelete(category);
    setDeleteModal(true);
  };

  // Add the refresh handler function
  const handleRefresh = async () => {
    try {
      setIsRefreshing(true);
      await refetchCategories();
      successToast("Categories refreshed successfully");
    } catch (error) {
      errorToast("Failed to refresh categories");
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="bg-white h-[calc(100vh-120px)]">
      <BreadCrumbsContainer />
      {/* Header Section with Search */}
      <div className="flex items-center justify-between px-6 py-4 border-b">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-semibold font-outfit text-gray-800">
            Product Categories
          </h1>
          <button
            onClick={() => setAddModal(true)}
            className="px-4 py-2 bg-[#5652B0] text-white rounded-lg text-sm font-medium hover:bg-[#4A47A3] flex items-center gap-2">
            <Plus size={16} />
            Add New Category
          </button>
        </div>

        {/* Search and Actions Section */}
        <div className="flex items-center gap-4">
          {/* Search Bar */}
          <div className="w-[550px]">
            <div className="relative">
              <SearchIcon
                className="absolute left-3 top-1/2 transform -translate-y-1/2"
                size={18}
                color="#83899F"
              />
              <input
                type="text"
                value={queryParams.search}
                onChange={e =>
                  setQueryParams(prev => ({ ...prev, search: e.target.value }))
                }
                placeholder="Search categories by name..."
                className="w-full h-10 pl-10 pr-10 rounded-lg border border-gray-200 
                         focus:border-[#5652B0] focus:ring-1 focus:ring-[#5652B0] 
                         outline-none transition-all text-gray-600"
              />
              {queryParams.search && (
                <button
                  onClick={() =>
                    setQueryParams(prev => ({ ...prev, search: "" }))
                  }
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 hover:bg-gray-100 p-1 rounded-full">
                  <X size={16} color="#83899F" />
                </button>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            <IconButton
              onClick={handleRefresh}
              className="hover:bg-gray-100"
              disabled={isRefreshing}
              title="Refresh">
              <RotateCw
                size={20}
                className={`text-gray-600 ${
                  isRefreshing ? "animate-spin" : ""
                }`}
              />
            </IconButton>
            <IconButton
              onClick={handleSettingsClick}
              className="hover:bg-gray-100"
              title="View Settings">
              <Settings size={20} className="text-gray-600" />
            </IconButton>
          </div>

          {/* Settings Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleCloseSettings}>
            <MenuItem
              onClick={() => {
                setViewType("grid");
                handleCloseSettings();
              }}>
              <GripVertical size={16} className="mr-2" />
              Grid View
            </MenuItem>
            <MenuItem
              onClick={() => {
                setViewType("list");
                handleCloseSettings();
              }}>
              <ListIcon size={16} className="mr-2" />
              List View
            </MenuItem>
          </Menu>
        </div>
      </div>

      {/* Filters Section */}
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4 flex-wrap">
            {/* Status Filter */}
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={queryParams.status}
                label="Status"
                onChange={e =>
                  setQueryParams(prev => ({ ...prev, status: e.target.value }))
                }
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#E5E7EB",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#5652B0",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#5652B0",
                  },
                }}>
                <MenuItem value="ALL">All Categories</MenuItem>
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="INACTIVE">Inactive</MenuItem>
              </Select>
            </FormControl>

            {/* Category Type Filter */}
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Category Type</InputLabel>
              <Select
                value={queryParams.parent_only}
                label="Category Type"
                onChange={e =>
                  setQueryParams(prev => ({
                    ...prev,
                    parent_only: e.target.value,
                  }))
                }
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#E5E7EB",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#5652B0",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#5652B0",
                  },
                }}>
                <MenuItem value={false}>All Types</MenuItem>
                <MenuItem value={true}>Parent Categories Only</MenuItem>
              </Select>
            </FormControl>

            {/* Sort By Filter */}
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={queryParams.sort}
                label="Sort By"
                onChange={e =>
                  setQueryParams(prev => ({ ...prev, sort: e.target.value }))
                }
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#E5E7EB",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#5652B0",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#5652B0",
                  },
                }}>
                <MenuItem value="">None</MenuItem>
                <MenuItem value="name_asc">Name (A-Z)</MenuItem>
                <MenuItem value="name_desc">Name (Z-A)</MenuItem>
                <MenuItem value="products_count_desc">
                  Products Count (High-Low)
                </MenuItem>
                <MenuItem value="products_count_asc">
                  Products Count (Low-High)
                </MenuItem>
                <MenuItem value="display_order_desc">
                  Display Order (High-Low)
                </MenuItem>
                <MenuItem value="display_order_asc">
                  Display Order (Low-High)
                </MenuItem>
              </Select>
            </FormControl>
          </div>

          {/* Clear Filters Button */}
          {(queryParams.search ||
            queryParams.status !== "ALL" ||
            queryParams.parent_only ||
            queryParams.sort) && (
            <button
              onClick={() =>
                setQueryParams({
                  status: "ALL",
                  search: "",
                  sort: "",
                  parent_only: false,
                  limit: 50,
                  page: 1,
                })
              }
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 bg-gray-50 
                       hover:bg-gray-100 rounded-lg transition-colors">
              <FilterX size={16} />
              Clear Filters
            </button>
          )}
        </div>

        {/* Categories Grid/List View */}
        {viewType === "list" ? (
          // List View
          <div className="bg-white rounded-lg border border-gray-200">
            {categories.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <p className="text-gray-500 mb-2">No categories found</p>
                <button
                  onClick={() => setAddModal(true)}
                  className="text-[#5652B0] hover:text-[#4A47A3] font-medium">
                  Add New Category
                </button>
              </div>
            ) : (
              categories.map(parentCategory => (
                <div
                  key={parentCategory.id}
                  className="border-b last:border-b-0">
                  {/* Parent Category Row */}
                  <div
                    className={`flex items-center justify-between px-6 py-4 hover:bg-gray-50 ${
                      expandedCategories[parentCategory.id] ? "bg-gray-50" : ""
                    }`}
                    onClick={() => toggleCategoryExpansion(parentCategory.id)}>
                    <div className="flex items-center flex-1">
                      <button className="p-1 hover:bg-gray-200 rounded-full mr-2">
                        {expandedCategories[parentCategory.id] ? (
                          <ChevronDown size={20} className="text-gray-500" />
                        ) : (
                          <ChevronRight size={20} className="text-gray-500" />
                        )}
                      </button>

                      <img
                        src={
                          parentCategory.image ||
                          require("../../../assets/brand.png")
                        }
                        alt={parentCategory.name}
                        className="w-10 h-10 rounded-full object-cover mr-4"
                        onError={e => {
                          e.target.onerror = null;
                          e.target.src = require("../../../assets/brand.png");
                        }}
                      />

                      <div className="flex-1">
                        <div className="flex items-center">
                          <h3 className="font-medium text-gray-900">
                            {parentCategory.name}
                          </h3>
                          <span className="ml-3 px-2.5 py-0.5 text-xs font-medium rounded-full bg-purple-50 text-[#5652B0]">
                            Parent Category
                          </span>
                          <span
                            className={`ml-2 px-2.5 py-0.5 text-xs font-medium rounded-full 
                            ${
                              parentCategory.status === "ACTIVE"
                                ? "bg-green-50 text-green-700"
                                : "bg-red-50 text-red-700"
                            }`}>
                            {parentCategory.status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500">
                          {parentCategory.children?.length || 0} subcategories •{" "}
                          {parentCategory.products} products • GST:{" "}
                          {parentCategory.gst}%
                        </p>
                      </div>

                      {/* Parent Category Actions */}
                      <div
                        className="flex items-center gap-2"
                        onClick={e => e.stopPropagation()}>
                        <button
                          onClick={() => handleViewCategory(parentCategory)}
                          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                          title="View Details">
                          <Eye size={18} className="text-gray-600" />
                        </button>
                        <button
                          onClick={() => handleEditClick(parentCategory)}
                          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                          title="Edit Category">
                          <Pencil size={18} className="text-gray-600" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(parentCategory)}
                          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                          title="Delete Category">
                          <Trash size={18} className="text-red-500" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Child Categories */}
                  {expandedCategories[parentCategory.id] &&
                    parentCategory.children?.length > 0 && (
                      <div className="border-t border-gray-100">
                        {parentCategory.children.map(childCategory => (
                          <div
                            key={childCategory.id}
                            className="flex items-center px-4 py-3 pl-14 hover:bg-gray-100">
                            <img
                              src={
                                childCategory.image ||
                                require("../../../assets/brand.png")
                              }
                              alt={childCategory.name}
                              className="w-8 h-8 rounded-full object-cover mr-4"
                              onError={e => {
                                e.target.onerror = null;
                                e.target.src = require("../../../assets/brand.png");
                              }}
                            />

                            <div className="flex-1">
                              <div className="flex items-center">
                                <h4 className="font-medium text-gray-900">
                                  {childCategory.name}
                                </h4>
                                <span
                                  className={`ml-3 px-2.5 py-0.5 text-xs font-medium rounded-full ${
                                    childCategory.status === "ACTIVE"
                                      ? "bg-green-50 text-green-700"
                                      : "bg-red-50 text-red-700"
                                  }`}>
                                  {childCategory.status}
                                </span>
                              </div>
                              <p className="text-sm text-gray-500">
                                {childCategory.products} products • GST:{" "}
                                {childCategory.gst}%
                              </p>
                            </div>

                            {/* Child Category Actions */}
                            <div className="flex items-center gap-2">
                              <button
                                onClick={e => {
                                  e.stopPropagation();
                                  handleViewCategory(childCategory);
                                }}
                                className="p-2 hover:bg-white rounded-full transition-colors"
                                title="View Details">
                                <Eye size={16} className="text-gray-600" />
                              </button>
                              <button
                                onClick={e => {
                                  e.stopPropagation();
                                  handleEditClick(childCategory);
                                }}
                                className="p-2 hover:bg-white rounded-full transition-colors"
                                title="Edit Category">
                                <Pencil size={16} className="text-gray-600" />
                              </button>
                              <button
                                onClick={e => {
                                  e.stopPropagation();
                                  handleDeleteClick(childCategory);
                                }}
                                className="p-2 hover:bg-white rounded-full transition-colors"
                                title="Delete Category">
                                <Trash size={16} className="text-red-500" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                </div>
              ))
            )}
          </div>
        ) : (
          // Grid View
          <div className="space-y-8">
            {categories.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <p className="text-gray-500 mb-2">No categories found</p>
              </div>
            ) : (
              categories.map(parentCategory => (
                <div key={parentCategory.id} className="space-y-4">
                  {/* Parent Category Header */}
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                      {parentCategory.name}
                      <span className="text-sm font-normal text-gray-500">
                        ({parentCategory.children?.length || 0} subcategories)
                      </span>
                    </h3>
                  </div>

                  {/* Grid of Categories */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {/* Parent Category Card */}
                    <CategoryCard
                      key={parentCategory.id}
                      category={parentCategory}
                      onView={() => handleViewCategory(parentCategory)}
                      onEdit={handleEditClick}
                      isParent={true}
                    />

                    {/* Child Categories */}
                    {parentCategory.children?.map(childCategory => (
                      <CategoryCard
                        key={childCategory.id}
                        category={childCategory}
                        onView={() => handleViewCategory(childCategory)}
                        onEdit={handleEditClick}
                        onDelete={handleDeleteClick}
                        isChild={true}
                        parentName={parentCategory.name}
                      />
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Add Category Modal */}
      <Modal open={addModal} onClose={() => setAddModal(false)}>
        <Box sx={modalStyle}>
          <div className="border-b border-gray-200">
            <div className="px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-medium text-gray-900">
                Add New Category
              </h2>
              <IconButton size="small" onClick={() => setAddModal(false)}>
                <X size={18} />
              </IconButton>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-2 gap-6">
              <TextField
                label="Category Name"
                variant="outlined"
                size="small"
                fullWidth
                required
                value={formData.name}
                onChange={e =>
                  setFormData(prev => ({ ...prev, name: e.target.value }))
                }
              />

              <TextField
                label="Parent Category"
                variant="outlined"
                size="small"
                select
                fullWidth
                value={formData.parent_category}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    parent_category: e.target.value,
                  }))
                }>
                <MenuItem value="">None (Top Level)</MenuItem>
                {categories
                  .filter(cat => !cat.parent) // Only show parent categories
                  .map(category => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
              </TextField>

              <TextField
                label="GST Rate (%)"
                variant="outlined"
                size="small"
                type="number"
                fullWidth
                required
                value={formData.gst_rate}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    gst_rate: Number(e.target.value),
                  }))
                }
              />

              <TextField
                label="Display Order"
                variant="outlined"
                size="small"
                type="number"
                fullWidth
                value={formData.display_order}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    display_order: Number(e.target.value),
                  }))
                }
              />

              <TextField
                label="Description"
                variant="outlined"
                size="small"
                multiline
                rows={3}
                fullWidth
                className="col-span-2"
                value={formData.description}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
              />

              <div className="col-span-2">
                <p className="text-sm font-medium text-gray-700 mb-3">
                  Category Image
                </p>
                <div className="flex items-start gap-6">
                  {/* Image Preview */}
                  <div className="w-32 h-32 rounded-lg border border-gray-200 overflow-hidden flex items-center justify-center bg-gray-50">
                    {previewUrl ? (
                      <img
                        src={previewUrl}
                        alt="Category Preview"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="text-center p-4">
                        <img
                          src={require("../../../assets/upload.png")}
                          alt="upload"
                          className="w-12 h-12 mx-auto mb-2 opacity-50"
                        />
                        <p className="text-xs text-gray-500">
                          No image selected
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Upload Controls */}
                  <div className="flex-1">
                    <div className="flex flex-col gap-3">
                      <div
                        className={`border-2 border-dashed rounded-lg transition-colors ${
                          previewUrl
                            ? "border-green-200 bg-green-50"
                            : "border-gray-200 hover:border-[#5652B0]"
                        }`}>
                        <label className="flex flex-col items-center justify-center py-4 px-6 cursor-pointer">
                          <div className="flex flex-col items-center">
                            {previewUrl ? (
                              <>
                                <div className="p-2 rounded-full bg-green-100 mb-2">
                                  <img
                                    src={require("../../../assets/upload.png")}
                                    alt="upload"
                                    className="w-5 h-5"
                                  />
                                </div>
                                <p className="text-sm font-medium text-green-600">
                                  Image selected
                                </p>
                                <p className="text-xs text-green-500 mt-1">
                                  Click to change image
                                </p>
                              </>
                            ) : (
                              <>
                                <div className="p-2 rounded-full bg-gray-100 mb-2">
                                  <img
                                    src={require("../../../assets/upload.png")}
                                    alt="upload"
                                    className="w-5 h-5"
                                  />
                                </div>
                                <p className="text-sm font-medium text-gray-700">
                                  Drop your image here, or{" "}
                                  <span className="text-[#5652B0]">browse</span>
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                  JPG, PNG up to 5MB
                                </p>
                              </>
                            )}
                          </div>
                          <input
                            type="file"
                            className="hidden"
                            accept=".jpg,.jpeg,.png"
                            onChange={handleImageSelect}
                          />
                        </label>
                      </div>

                      {/* Image Requirements */}
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs font-medium text-gray-600 mb-2">
                          Image Requirements:
                        </p>
                        <ul className="text-xs text-gray-500 space-y-1">
                          <li className="flex items-center">
                            <span className="w-1 h-1 rounded-full bg-gray-400 mr-2" />
                            Square aspect ratio (1:1) recommended
                          </li>
                          <li className="flex items-center">
                            <span className="w-1 h-1 rounded-full bg-gray-400 mr-2" />
                            Minimum resolution: 512x512 pixels
                          </li>
                          <li className="flex items-center">
                            <span className="w-1 h-1 rounded-full bg-gray-400 mr-2" />
                            Maximum file size: 5MB
                          </li>
                        </ul>
                      </div>

                      {/* Remove Image Button */}
                      {previewUrl && (
                        <button
                          onClick={() => {
                            setSelectedImage(null);
                            setPreviewUrl(null);
                          }}
                          className="text-sm text-red-600 hover:text-red-700 flex items-center justify-center py-1">
                          Remove image
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setAddModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                disabled={isCreating}>
                Cancel
              </button>
              <button
                onClick={handleAddCategory}
                disabled={isCreating}
                className="px-4 py-2 bg-[#5652B0] text-white rounded-lg text-sm font-medium hover:bg-[#4A47A3] disabled:bg-purple-300 flex items-center gap-2">
                {isCreating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Add Category"
                )}
              </button>
            </div>
          </div>
        </Box>
      </Modal>

      {/* View Category Details Modal */}
      <Modal open={viewModal} onClose={() => setViewModal(false)}>
        <Box sx={modalStyle}>
          <div className="border-b border-gray-200">
            <div className="px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-medium text-gray-900">
                Category Details
              </h2>
              <IconButton size="small" onClick={() => setViewModal(false)}>
                <X size={18} />
              </IconButton>
            </div>
          </div>

          {selectedCategory && (
            <div className="p-6">
              <div className="flex items-start gap-6">
                {/* Category Image */}
                <div className="w-32 h-32">
                  <img
                    src={
                      selectedCategory.image ||
                      require("../../../assets/brand.png")
                    }
                    alt={selectedCategory.name}
                    className="w-full h-full rounded-lg object-cover"
                    onError={e => {
                      e.target.onerror = null;
                      e.target.src = require("../../../assets/brand.png");
                    }}
                  />
                </div>

                {/* Category Details */}
                <div className="flex-1 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-medium text-gray-900">
                      {selectedCategory.name}
                    </h3>
                    <span
                      className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                        selectedCategory.status === "ACTIVE"
                          ? "bg-green-50 text-green-700"
                          : "bg-red-50 text-red-700"
                      }`}>
                      {selectedCategory.status}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">GST Rate</p>
                      <p className="font-medium">{selectedCategory.gst}%</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Products</p>
                      <p className="font-medium">{selectedCategory.products}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Created At</p>
                      <p className="font-medium">
                        {new Date(
                          selectedCategory.createdAt,
                        ).toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Last Updated</p>
                      <p className="font-medium">
                        {new Date(
                          selectedCategory.updatedAt,
                        ).toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  {selectedCategory.description && (
                    <div>
                      <p className="text-gray-500 text-sm">Description</p>
                      <p className="mt-1 text-gray-700">
                        {selectedCategory.description}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </Box>
      </Modal>

      {/* Edit Category Modal */}
      <Modal open={editModal} onClose={() => setEditModal(false)}>
        <Box sx={modalStyle}>
          <div className="border-b border-gray-200">
            <div className="px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-medium text-gray-900">
                Edit Category
              </h2>
              <IconButton size="small" onClick={() => setEditModal(false)}>
                <X size={18} />
              </IconButton>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-2 gap-6">
              <TextField
                label="Category Name"
                variant="outlined"
                size="small"
                fullWidth
                required
                value={editFormData.name}
                onChange={e =>
                  setEditFormData(prev => ({ ...prev, name: e.target.value }))
                }
              />

              <TextField
                label="Parent Category"
                variant="outlined"
                size="small"
                select
                fullWidth
                value={editFormData.parent_category}
                onChange={e =>
                  setEditFormData(prev => ({
                    ...prev,
                    parent_category: e.target.value,
                  }))
                }>
                <MenuItem value="">None (Top Level)</MenuItem>
                {categories
                  .filter(cat => !cat.parent && cat.id !== editingCategory?.id)
                  .map(category => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
              </TextField>

              <TextField
                label="GST Rate (%)"
                variant="outlined"
                size="small"
                type="number"
                fullWidth
                required
                value={editFormData.gst_rate}
                onChange={e =>
                  setEditFormData(prev => ({
                    ...prev,
                    gst_rate: Number(e.target.value),
                  }))
                }
              />

              <TextField
                label="Display Order"
                variant="outlined"
                size="small"
                type="number"
                fullWidth
                value={editFormData.display_order}
                onChange={e =>
                  setEditFormData(prev => ({
                    ...prev,
                    display_order: Number(e.target.value),
                  }))
                }
              />

              <TextField
                label="Status"
                variant="outlined"
                size="small"
                select
                fullWidth
                value={editFormData.status}
                onChange={e =>
                  setEditFormData(prev => ({
                    ...prev,
                    status: e.target.value,
                  }))
                }>
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="INACTIVE">Inactive</MenuItem>
              </TextField>

              <TextField
                label="Description"
                variant="outlined"
                size="small"
                multiline
                rows={3}
                fullWidth
                className="col-span-2"
                value={editFormData.description}
                onChange={e =>
                  setEditFormData(prev => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
              />

              <div className="col-span-2">
                <p className="text-sm font-medium text-gray-700 mb-3">
                  Category Image
                </p>
                <div className="flex items-start gap-6">
                  {/* Image Preview */}
                  <div className="w-32 h-32 rounded-lg border border-gray-200 overflow-hidden flex items-center justify-center bg-gray-50">
                    {editPreviewUrl ? (
                      <img
                        src={editPreviewUrl}
                        alt="Category Preview"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="text-center p-4">
                        <img
                          src={require("../../../assets/upload.png")}
                          alt="upload"
                          className="w-12 h-12 mx-auto mb-2 opacity-50"
                        />
                        <p className="text-xs text-gray-500">
                          No image selected
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Upload Controls */}
                  <div className="flex-1">
                    <div className="flex flex-col gap-3">
                      <div
                        className={`border-2 border-dashed rounded-lg transition-colors ${
                          editPreviewUrl
                            ? "border-green-200 bg-green-50"
                            : "border-gray-200 hover:border-[#5652B0]"
                        }`}>
                        <label className="flex flex-col items-center justify-center py-4 px-6 cursor-pointer">
                          <div className="flex flex-col items-center">
                            {editPreviewUrl ? (
                              <>
                                <div className="p-2 rounded-full bg-green-100 mb-2">
                                  <img
                                    src={require("../../../assets/upload.png")}
                                    alt="upload"
                                    className="w-5 h-5"
                                  />
                                </div>
                                <p className="text-sm font-medium text-green-600">
                                  Image selected
                                </p>
                                <p className="text-xs text-green-500 mt-1">
                                  Click to change image
                                </p>
                              </>
                            ) : (
                              <>
                                <div className="p-2 rounded-full bg-gray-100 mb-2">
                                  <img
                                    src={require("../../../assets/upload.png")}
                                    alt="upload"
                                    className="w-5 h-5"
                                  />
                                </div>
                                <p className="text-sm font-medium text-gray-700">
                                  Drop your image here, or{" "}
                                  <span className="text-[#5652B0]">browse</span>
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                  JPG, PNG up to 5MB
                                </p>
                              </>
                            )}
                          </div>
                          <input
                            type="file"
                            className="hidden"
                            accept=".jpg,.jpeg,.png"
                            onChange={handleEditImageSelect}
                          />
                        </label>
                      </div>

                      {/* Image Requirements */}
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs font-medium text-gray-600 mb-2">
                          Image Requirements:
                        </p>
                        <ul className="text-xs text-gray-500 space-y-1">
                          <li className="flex items-center">
                            <span className="w-1 h-1 rounded-full bg-gray-400 mr-2" />
                            Square aspect ratio (1:1) recommended
                          </li>
                          <li className="flex items-center">
                            <span className="w-1 h-1 rounded-full bg-gray-400 mr-2" />
                            Minimum resolution: 512x512 pixels
                          </li>
                          <li className="flex items-center">
                            <span className="w-1 h-1 rounded-full bg-gray-400 mr-2" />
                            Maximum file size: 5MB
                          </li>
                        </ul>
                      </div>

                      {/* Remove Image Button */}
                      {editPreviewUrl && (
                        <button
                          onClick={() => {
                            setEditImage(null);
                            setEditPreviewUrl(null);
                          }}
                          className="text-sm text-red-600 hover:text-red-700 flex items-center justify-center py-1">
                          Remove image
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setEditModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                disabled={isEditing}>
                Cancel
              </button>
              <button
                onClick={handleEditCategory}
                disabled={isEditing}
                className="px-4 py-2 bg-[#5652B0] text-white rounded-lg text-sm font-medium hover:bg-[#4A47A3] disabled:bg-purple-300 flex items-center gap-2">
                {isEditing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Updating...
                  </>
                ) : (
                  "Save Changes"
                )}
              </button>
            </div>
          </div>
        </Box>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal open={deleteModal} onClose={() => setDeleteModal(false)}>
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Overlay */}
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

          {/* Modal */}
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              {/* Close button */}
              <button
                onClick={() => {
                  setDeleteModal(false);
                  setCategoryToDelete(null);
                }}
                className="absolute right-4 top-4 text-gray-400 hover:text-gray-500">
                <X className="h-5 w-5" />
              </button>

              <div className="sm:flex sm:items-start">
                {/* Icon */}
                <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 bg-red-100">
                  <Trash className="h-6 w-6 text-red-600" />
                </div>

                {/* Content */}
                <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                  <h3 className="text-base font-semibold leading-6 text-gray-900">
                    Delete Category
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Are you sure you want to delete{" "}
                      <span className="font-medium text-gray-900">
                        {categoryToDelete?.name}
                      </span>
                      ? This action will:
                      <ul className="mt-2 list-disc pl-5 space-y-1">
                        <li>Permanently delete all category data</li>
                        {categoryToDelete?.products > 0 && (
                          <li>
                            Remove category from {categoryToDelete.products}{" "}
                            products
                          </li>
                        )}
                        {categoryToDelete?.children?.length > 0 && (
                          <li>
                            Delete {categoryToDelete.children.length}{" "}
                            subcategories
                          </li>
                        )}
                      </ul>
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse gap-3">
                <button
                  type="button"
                  onClick={handleDeleteCategory}
                  disabled={isDeleting}
                  className={`inline-flex w-full justify-center rounded-lg px-3 py-2 text-sm font-semibold text-white shadow-sm sm:w-auto bg-red-600 hover:bg-red-500 ${
                    isDeleting ? "opacity-50 cursor-not-allowed" : ""
                  }`}>
                  {isDeleting ? (
                    <div className="flex items-center">
                      <div className="h-4 w-4 mr-2 rounded-full border-2 border-white border-t-transparent animate-spin" />
                      Deleting...
                    </div>
                  ) : (
                    "Delete Category"
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDeleteModal(false);
                    setCategoryToDelete(null);
                  }}
                  disabled={isDeleting}
                  className="mt-3 inline-flex w-full justify-center rounded-lg bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Category;
